import * as React from 'react';
import MapLegacy from './GISComponents/MapLegacy';
import TractAttributeTable, { LeaseInfo, TractDetailPanel } from './GISComponents/TractAttributeTable';
import { AppContext } from 'src/providers/AppContextProvider';
import { IAppContextState } from 'src/reducers/AppContext';
import useFetch from 'use-http';
import TractDetail from 'src/models/tract_detail/TractDetail';
import Leasehold from 'src/models/leasehold/Leasehold';
import Ownership from 'src/models/tract_detail/Ownership';
import ContactNote from 'src/models/ContactNote';
import { ODataOptions } from 'src/util/ODataBuilder';
import { useSelector } from 'react-redux';
import { IRootState } from 'src/reducers';
import { LandSystem } from 'src/models/LandSystem';
import {
    errorDetails,
    IMapIssue,
    IRequestOutcome,
    loadFailureIssue,
    mapLoadFailureIssue,
    MapLoadStep,
    requestOutcome,
    tractLoadNote,
} from './GISComponents/mapHealth';
import { MapBlockOverlay, MapNotice } from './GISComponents/MapStatus';
import SoftErrorBoundary from './GISComponents/SoftErrorBoundary';
import { IMorLease, IMorListing, IMorOwner, isBrineLayerId, morRoute } from './GISComponents/morData';
import { contractLayerPrefixOf } from './GISComponents/MapConfig';

import './GISComponents/MapStyling.scss';

/** Parent-Lease lookups in flight at once for the Leaseholds tab (see handleLeaseholdsShown). */
const LEASE_LOOKUPS_AT_ONCE = 2;

/* The map opens on the lower 48: a handful of low-zoom basemap tiles and no tract data, so it comes
 * up quickly. It moves to the project's tracts once they load. Web Mercator, the basemap's own
 * coordinates, so nothing needs projecting. */
const LOWER_48_VIEW: __esri.MapViewProperties = {
    extent: { xmin: -13914936, ymin: 2753408, xmax: -7402746, ymax: 6360131, spatialReference: { wkid: 3857 } },
};

const MappingPage: React.FC = () => {
    const appContext = React.useContext<IAppContextState>(AppContext);
    // Each hook's `request` carries its live error, the only reliable sign a request failed (see
    // requestOutcome in mapHealth).
    const { get, response, request: mapDataRequest } = useFetch();
    const { get: getTractDetail, request: tractDetailRequest } = useFetch();
    const { get: getTractLeaseholds, request: leaseholdsRequest } = useFetch();
    const { get: getOwnerships, request: ownershipsRequest } = useFetch();
    const { get: getLease } = useFetch();
    const getLeaseRef = React.useRef(getLease);
    getLeaseRef.current = getLease;
    const { get: getContactNotes, request: contactNotesRequest } = useFetch();
    const getContactNotesRef = React.useRef(getContactNotes);
    getContactNotesRef.current = getContactNotes;
    const contactNotesRequestRef = React.useRef(contactNotesRequest);
    contactNotesRequestRef.current = contactNotesRequest;
    // The Mineral Ownership Report's listings for the selected tract (see handleMorShown), one hook each.
    const { get: getMorOwners, request: morOwnersRequest } = useFetch();
    const { get: getMorLeases, request: morLeasesRequest } = useFetch();
    const { get: getMorBrineLeases, request: morBrineLeasesRequest } = useFetch();
    const morFetchRef = React.useRef({
        owners: { get: getMorOwners, request: morOwnersRequest },
        leases: { get: getMorLeases, request: morLeasesRequest },
        'brine-leases': { get: getMorBrineLeases, request: morBrineLeasesRequest },
    });
    morFetchRef.current = {
        owners: { get: getMorOwners, request: morOwnersRequest },
        leases: { get: getMorLeases, request: morLeasesRequest },
        'brine-leases': { get: getMorBrineLeases, request: morBrineLeasesRequest },
    };
    // Parent Lease date + lessee per lease id (see LeaseInfo). Cached for the session: a lease
    // usually spans several tracts, so clicking around shouldn't re-fetch it.
    const leaseInfoCacheRef = React.useRef<Record<number, LeaseInfo>>({});
    const leaseRequestsPendingRef = React.useRef<Set<number>>(new Set());
    // Lease lookups still waiting to start. Replaced, dropping the rest, whenever another tract is
    // selected, so clicking around never leaves a backlog in front of the next tract's requests.
    const leaseQueueRef = React.useRef<{ seq: number; ids: number[] }>({ seq: 0, ids: [] });
    const [leaseInfo, setLeaseInfo] = React.useState<Record<number, LeaseInfo>>({});
    // Client/project of the selected tract, for the lease requests made when its Leaseholds tab opens.
    const selectedTractRouteRef = React.useRef<{
        tractId: string;
        clientNumber: string;
        projectNumber: string;
        sectionNumber: string;
        tractNumber: string;
        horizonNumber: string;
    } | null>(null);
    // The selected tract's MOR listings, and which tract each was last asked for.
    const [morOwners, setMorOwners] = React.useState<IMorListing<IMorOwner> | undefined>(undefined);
    const [morLeases, setMorLeases] = React.useState<IMorListing<IMorLease> | undefined>(undefined);
    const [morBrineLeases, setMorBrineLeases] = React.useState<IMorListing<IMorLease> | undefined>(undefined);
    const morRequestedRef = React.useRef<Record<string, string>>({});
    // The map's tract layers, once it reports them: the panel shows a Leaseholds tab per layer that has one.
    const [tractLayerIds, setTractLayerIds] = React.useState<string[] | undefined>(undefined);
    const [selectedKuduTractID, setSelectedKuduTractID] = React.useState<string | undefined>(undefined);
    const [apiKey, setApiKey] = React.useState<string>(undefined);
    const [mapId, setMapId] = React.useState<string>(undefined);
    const [tractsUrl, setTractsUrl] = React.useState<string>(undefined);
    const [aoiUrl, setAoiUrl] = React.useState<string>(undefined);
    const [isDataLoaded, setIsDataLoaded] = React.useState<boolean>(false);
    const [configUrl, setConfigUrl] = React.useState<string>(undefined);
    // Map-wide problems (see mapHealth): a blocking one grays the page out, a notice sits over the map.
    const [mapBlock, setMapBlock] = React.useState<IMapIssue | undefined>(undefined);
    const [mapNotice, setMapNotice] = React.useState<IMapIssue | undefined>(undefined);
    // The selected tract's details failed to load: the panel says so instead of showing stale data.
    // Each tract selection's number, so a slower earlier selection can't land over a later one.
    const tractSelectSeqRef = React.useRef(0);
    // The panel's note when some of the selected tract's records didn't load (see tractLoadNote).
    const [tractNote, setTractNote] = React.useState<string | undefined>(undefined);

    // Attribute table state
    const [selectedAttributes, setSelectedAttributes] = React.useState<Record<string, string | number> | undefined>(undefined);
    const [tractDetail, setTractDetail] = React.useState<TractDetail | undefined>(undefined);
    const [leaseholdData, setLeaseholdData] = React.useState<Leasehold[] | undefined>(undefined);
    const [mineralOwnerships, setMineralOwnerships] = React.useState<Ownership[] | undefined>(undefined);
    const [isLoadingDetail, setIsLoadingDetail] = React.useState<boolean>(false);
    const [isLoadingLeaseholds, setIsLoadingLeaseholds] = React.useState<boolean>(false);
    const [isLoadingOwnerships, setIsLoadingOwnerships] = React.useState<boolean>(false);
    const [isTableCollapsed, setIsTableCollapsed] = React.useState<boolean>(true);
    const [isDetailCollapsed, setIsDetailCollapsed] = React.useState<boolean>(true);
    const [contactCategoryColors, setContactCategoryColors] = React.useState<Record<string, number[]>>({});
    // Owner cards only show Contact Category swatches while that layer is the one on the map;
    // under Leasehold or Title Status the same colors would mean something else.
    const [activeTractLayerId, setActiveTractLayerId] = React.useState<string | undefined>(undefined);
    const [leaseholdColors, setLeaseholdColors] = React.useState<Record<string, number[]>>({});
    // Each contract layer's WI owner colors, by prefix ('Brine'), as the map drew them.
    const [contractLayerColors, setContractLayerColors] = React.useState<Record<string, Record<string, number[]>>>({});
    const handleContractLayerColors = React.useCallback((prefix: string, colors: Record<string, number[]>) => {
        setContractLayerColors((all) => ({ ...all, [prefix]: colors }));
    }, []);
    const [titleStatusColors, setTitleStatusColors] = React.useState<Record<string, number[]>>({});
    const [allTracts, setAllTracts] = React.useState<Record<string, string | number>[]>([]);
    // Multi-select state — lifted here so both the map (drives selection via rectangle)
    // and the attribute table (displays + filters by it) can read/write.
    const [selectedTractIds, setSelectedTractIds] = React.useState<string[]>([]);
    const selectTractRef = React.useRef<((tractId: string) => void) | null>(null);
    const zoomToTractRef = React.useRef<((tractId: string) => void) | null>(null);

    const handleSelectKuduTractID = (gid: string) => {
        setSelectedKuduTractID(gid);
    };

    const parseKuduTractID = (
        id: string
    ): { clientNumber: string; projectNumber: string; sectionNumber: string; tractNumber: string; horizonNumber: string } | null => {
        if (!id) {
            return null;
        }
        const dashParts = id.split('-');
        if (dashParts.length < 2) {
            return null;
        }
        const horizonNumber = dashParts[dashParts.length - 1];
        const dotPart = dashParts.slice(0, -1).join('-');
        const dotParts = dotPart.split('.');
        if (dotParts.length < 4) {
            return null;
        }
        return {
            clientNumber: dotParts[0],
            projectNumber: dotParts[1],
            sectionNumber: dotParts[2],
            tractNumber: dotParts[3],
            horizonNumber,
        };
    };

    /* Called by the Leaseholds tab when it's shown, so tracts viewed on other tabs never touch the
     * lease endpoint. Fetches the parent Lease, through the existing GET leases/{leaseId}, only for
     * in-hand leaseholds (drafts aren't shown) that are missing something: no lease date, or no WI
     * owner (the lessee stands in). Once per lease id: that endpoint is shared and heavy (it loads the
     * whole lease: clauses, tracts, every leasehold and its owner), so complete leaseholds skip it.
     * The tab asks only after its own data has loaded, so the cards never wait on these; date and
     * lessee fill in as they land. They run LEASE_LOOKUPS_AT_ONCE at a time: the browser allows 6
     * open requests to the API, and the next tract's own requests mustn't queue behind them.
     * Stable identity (refs only), so the tab's effect doesn't re-run on every render. */
    const leaseholdNeedsLease = (lh: Leasehold): boolean => !lh.leaseDate || !lh.wiOwners || lh.wiOwners.length === 0;
    const handleLeaseholdsShown = React.useCallback((leaseholds: Leasehold[]) => {
        const route = selectedTractRouteRef.current;
        const cache = leaseInfoCacheRef.current;
        const pending = leaseRequestsPendingRef.current;
        if (!route) {
            return;
        }
        const ids = Array.from(new Set(leaseholds.filter((lh) => lh.isInHand && lh.leaseId && leaseholdNeedsLease(lh)).map((lh) => lh.leaseId)));
        const seq = leaseQueueRef.current.seq + 1;
        leaseQueueRef.current = { seq, ids: ids.filter((leaseId) => !(leaseId in cache) && !pending.has(leaseId)) };

        const startNext = () => {
            const queue = leaseQueueRef.current;
            if (queue.seq !== seq) {
                return; // another tract (or a re-show) took over; its queue replaces this one
            }
            const leaseId = queue.ids.shift();
            if (leaseId === undefined) {
                return;
            }
            pending.add(leaseId);
            getLeaseRef
                .current(`v1.0/clients/${route.clientNumber}/projects/${route.projectNumber}/leases/${leaseId}`)
                .then((lease) => {
                    // After a network error use-http hands back the previous lease, so only this
                    // lease's own record counts; a failed lookup is tried again next time.
                    if (lease && lease.id === leaseId) {
                        cache[leaseId] = { leaseDate: lease.leaseDate, lesseeName: lease.lesseeName };
                        setLeaseInfo({ ...cache });
                    }
                })
                .catch((err) => console.log('Failed to fetch lease:', leaseId, err))
                // Runs either way: the catch above settles the chain. Not .finally, which the es6 lib
                // in tsconfig lacks, so the webpack type check (dev server and production build) rejects it.
                .then(() => {
                    pending.delete(leaseId);
                    startNext();
                });
        };
        for (let i = 0; i < LEASE_LOOKUPS_AT_ONCE; i++) {
            startNext();
        }
    }, []);

    /* One owner's contact notes for the Mineral Owners drill-in: fetched when that owner's card is
     * opened, never per card. GET contact-notes/owner/{ownerLookUpId} returns all of the owner's
     * notes (the source the Owners page uses); without an owner id, the ownership's own note. */
    const handleLoadContactNotes = React.useCallback((ownership: Ownership): Promise<ContactNote[]> => {
        const route = selectedTractRouteRef.current;
        if (!route) {
            return Promise.resolve([]);
        }
        const base = `v1.0/clients/${route.clientNumber}/projects/${route.projectNumber}/contact-notes`;
        // After a network error use-http hands back the previous owner's notes; fail instead, so the
        // drill-in shows its error rather than someone else's notes.
        const failed = () => requestOutcome(contactNotesRequestRef.current.error).failed;
        if (ownership.ownerLookUpId) {
            return getContactNotesRef.current(`${base}/owner/${ownership.ownerLookUpId}`).then((result) => {
                if (failed() || !Array.isArray(result)) {
                    throw new Error('Contact notes request failed');
                }
                return result as ContactNote[];
            });
        }
        return getContactNotesRef.current(`${base}/ownership/${ownership.id}`).then((result) => {
            if (failed()) {
                throw new Error('Contact notes request failed');
            }
            return result && result.id ? [result as ContactNote] : [];
        });
    }, []);

    /* Called by a tab when it shows, for the MOR listing it reads: the owners (Mineral Owners), the oil
     * and gas leases (Leaseholds O&G) or the brine leases (Leaseholds Brine). Each loads once per
     * tract; a failed load is asked for again the next time the tab shows (or on its Try again), and
     * a slow listing for an earlier tract is dropped when it lands. Stable identity (refs only). */
    const handleMorShown = React.useCallback((kind: 'owners' | 'leases' | 'brine-leases') => {
        const route = selectedTractRouteRef.current;
        if (!route || morRequestedRef.current[kind] === route.tractId) {
            return;
        }
        const { tractId } = route;
        morRequestedRef.current[kind] = tractId;
        const setListing = (listing: IMorListing<IMorOwner> | IMorListing<IMorLease>) => {
            if (kind === 'owners') {
                setMorOwners(listing as IMorListing<IMorOwner>);
            } else if (kind === 'leases') {
                setMorLeases(listing as IMorListing<IMorLease>);
            } else {
                setMorBrineLeases(listing as IMorListing<IMorLease>);
            }
        };
        const settle = (failed: boolean, rows: unknown) => {
            if (selectedTractRouteRef.current?.tractId !== tractId) {
                return;
            }
            if (failed) {
                morRequestedRef.current[kind] = '';
            }
            setListing({ tractId, status: failed ? 'error' : 'ready', rows: !failed && Array.isArray(rows) ? rows : [] });
        };
        const fetcher = morFetchRef.current[kind];
        setListing({ tractId, status: 'loading', rows: [] });
        fetcher
            .get(morRoute(route, kind === 'owners' ? 'mor-owners' : 'mor-leases', kind === 'brine-leases'))
            // After a network error use-http hands back the hook's previous data; its error says so.
            .then((rows) => settle(requestOutcome(fetcher.request.error).failed, rows))
            .catch(() => settle(true, []));
    }, []);

    const handleTractSelect = async (id: string, attributes: Record<string, string | number>) => {
        // The tract's route and a clean slate for its MOR listings come first, before any state
        // changes. A map click runs this outside React's batching, so each update below renders at
        // once, and a tab that mounts for the new tract asks for its listing (handleMorShown) before
        // this function goes on. It must already find this tract's route, not the last one's, or the
        // listing is never asked for and the tab waits on it for good.
        const parsed = parseKuduTractID(id);
        selectedTractRouteRef.current = parsed ? { tractId: id, ...parsed } : null;
        // The new tract's MOR listings load when their tabs show.
        morRequestedRef.current = {};
        setMorOwners(undefined);
        setMorLeases(undefined);
        setMorBrineLeases(undefined);

        setSelectedAttributes(attributes);
        setSelectedKuduTractID(id);
        setIsDetailCollapsed(false);

        if (!parsed) {
            return;
        }

        const { clientNumber, projectNumber, sectionNumber, tractNumber, horizonNumber } = parsed;
        // Drop the previous tract's lease lookups that haven't started yet.
        leaseQueueRef.current = { seq: leaseQueueRef.current.seq + 1, ids: [] };

        // A newer selection supersedes this one: its requests' results are ignored when they land.
        const seq = ++tractSelectSeqRef.current;
        const isCurrent = () => seq === tractSelectSeqRef.current;
        // The requests that failed, for the panel's note. After a network error use-http hands back
        // the hook's previous data (the previous tract's), so a failed request sets nothing.
        const failures: Array<{ outcome: IRequestOutcome; step: MapLoadStep }> = [];
        const noteFailure = (error: unknown, step: MapLoadStep): boolean => {
            const outcome = requestOutcome(error);
            if (outcome.failed) {
                failures.push({ outcome, step });
            }
            return outcome.failed;
        };

        // Reset and start loading in parallel
        setTractNote(undefined);
        setIsLoadingDetail(true);
        setIsLoadingLeaseholds(true);
        setIsLoadingOwnerships(true);
        setTractDetail(undefined);
        setLeaseholdData(undefined);
        setMineralOwnerships(undefined);

        // Fetch tract detail and leaseholds in parallel using separate useFetch hooks
        const detailPromise = getTractDetail(
            `v1.0/clients/${clientNumber}/projects/${projectNumber}/fields/${sectionNumber}/tracts/${tractNumber}/horizons/${horizonNumber}`
        )
            .then((detail) => {
                if (!isCurrent()) {
                    return;
                }
                if (!noteFailure(tractDetailRequest.error, 'tract-detail') && detail) {
                    setTractDetail(detail);
                }
                setIsLoadingDetail(false);
            })
            .catch((err) => {
                console.log('Failed to fetch tract detail:', err);
                failures.push({ outcome: { failed: true }, step: 'tract-detail' });
                if (isCurrent()) {
                    setIsLoadingDetail(false);
                }
            });

        const leaseholdsPromise = getTractLeaseholds(
            `v1.0/clients/${clientNumber}/projects/${projectNumber}/fields/${sectionNumber}/tracts/${tractNumber}/leaseholds`
        )
            .then((leaseholds) => {
                if (!isCurrent()) {
                    return;
                }
                if (!noteFailure(leaseholdsRequest.error, 'leaseholds')) {
                    if (leaseholds) {
                        const data = Array.isArray(leaseholds) ? leaseholds : ((leaseholds as Record<string, unknown>).items as Leasehold[]) || [];
                        setLeaseholdData(data);
                    } else {
                        setLeaseholdData([]);
                    }
                } else {
                    // Empty, not loading: the tab shows no leaseholds and the panel note says why.
                    setLeaseholdData([]);
                }
                setIsLoadingLeaseholds(false);
            })
            .catch((err) => {
                console.log('Failed to fetch leaseholds:', err);
                failures.push({ outcome: { failed: true }, step: 'leaseholds' });
                if (isCurrent()) {
                    setLeaseholdData([]);
                    setIsLoadingLeaseholds(false);
                }
            });

        // Mineral ownerships come from the paged-ownerships endpoint — the tract-detail
        // endpoint does NOT populate per-horizon ownership. One page large enough to hold
        // all rows for the horizon (mineral owners per tract are modest).
        const ownershipOptions = new ODataOptions().addPaging(1, 1000);
        const ownershipsPromise = getOwnerships(
            `v1.0/clients/${clientNumber}/projects/${projectNumber}/fields/${sectionNumber}/tracts/${tractNumber}/horizons/${horizonNumber}/paged-ownerships/${ownershipOptions.toString()}`
        )
            .then((result) => {
                if (!isCurrent()) {
                    return;
                }
                if (!noteFailure(ownershipsRequest.error, 'mineral-owners')) {
                    const items =
                        result && Array.isArray((result as Record<string, unknown>).items)
                            ? ((result as Record<string, unknown>).items as Ownership[])
                            : Array.isArray(result)
                            ? (result as Ownership[])
                            : [];
                    setMineralOwnerships(items);
                } else {
                    setMineralOwnerships([]);
                }
                setIsLoadingOwnerships(false);
            })
            .catch((err) => {
                console.log('Failed to fetch ownerships:', err);
                failures.push({ outcome: { failed: true }, step: 'mineral-owners' });
                if (isCurrent()) {
                    setMineralOwnerships([]);
                    setIsLoadingOwnerships(false);
                }
            });

        await Promise.all([detailPromise, leaseholdsPromise, ownershipsPromise]);
        // A tract whose records didn't all load keeps the panel open on what did load and what the map
        // file has, with a note saying what's missing (or that LandHawk has no such tract); the console
        // keeps the details.
        if (isCurrent() && failures.length) {
            failures.forEach((f) =>
                console.log(
                    'Tract records failed to load:',
                    errorDetails({ step: f.step, status: f.outcome.status, at: new Date() }, `Tract: ${id}`).join('; ')
                )
            );
            setTractNote(tractLoadNote(failures.map((f) => ({ step: f.step, status: f.outcome.status }))));
        }
    };

    // The map couldn't start, or its tracts downloaded but couldn't be read or drawn (reported by
    // MapLegacy, or by the tract list's own read of the file). The first problem found is the one shown.
    const handleMapLoadFailed = React.useCallback((step: MapLoadStep) => {
        setMapBlock((current) => current ?? mapLoadFailureIssue(step));
    }, []);

    // The side panel shuts while a problem blocks the map, so no error shows in it.
    React.useEffect(() => {
        if (mapBlock) {
            setIsDetailCollapsed(true);
        }
    }, [mapBlock]);

    // How the side panel names the selected tract depends on the project's land system. PLSS
    // (Jeffersonian) reads "**Tract 5** · Sec 3 T12N R14W", section first (the tract number is the
    // KuduTractID part before the dash). Other systems (Texas, Eastern, ROW) keep the KuduTractID for now.
    const landSystemId = useSelector((state: IRootState) => state.project?.currentProject?.landSystemId);
    const tractTitle = React.useMemo(() => {
        if (!selectedKuduTractID) {
            return undefined;
        }
        const parsed = parseKuduTractID(selectedKuduTractID);
        if (landSystemId !== LandSystem.Jeffersonian || !parsed) {
            // Panel default: "Tract: {KuduTractID}" (and no separate small ID line).
            return undefined;
        }
        // Section/township/range from the tract record: its `section` is Section.Number, the real
        // PLSS section. NOT the KuduTractID's section part (Section.No, LandHawk's index for a
        // T/R/S) — and not the GeoJSON Location either, which is built from that index.
        const str = [
            tractDetail?.section ? `Sec ${tractDetail.section}` : '',
            tractDetail?.township ? `T${tractDetail.township}` : '',
            tractDetail?.range ? `R${tractDetail.range}` : '',
        ]
            .filter(Boolean)
            .join(' ');
        return (
            <>
                <strong style={{ fontWeight: 700 }}>Tract {parsed.tractNumber}</strong>
                {str && <span style={{ fontWeight: 400 }}>{` · ${str}`}</span>}
            </>
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedKuduTractID, landSystemId, tractDetail]);

    const handleNavigateToTractPage = () => {
        if (!selectedKuduTractID) {
            return;
        }
        const parsed = parseKuduTractID(selectedKuduTractID);
        if (!parsed) {
            return;
        }
        const { clientNumber, projectNumber, sectionNumber, tractNumber, horizonNumber } = parsed;
        const path = `/clients/${clientNumber}/projects/${projectNumber}/sections/${sectionNumber}/tracts/${tractNumber}/horizons/${horizonNumber}`;
        window.open(path, '_blank');
    };

    const getMapData = async () => {
        setIsDataLoaded(false);
        setMapBlock(undefined);
        setMapNotice(undefined);
        const result = await get(`v1.0/gis/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/projectMapByNumber`);

        // Every check reads the hook's error (see requestOutcome): after a network error its `response`
        // is still the previous request's. Any outcome renders the page, with the problem shown on it.
        const recordLoad = requestOutcome(mapDataRequest.error);
        if (recordLoad.failed) {
            console.log('Map settings request failed:', recordLoad.status ?? 'no response');
            setMapBlock(loadFailureIssue(recordLoad, 'map-record'));
            setIsDataLoaded(true);
            return;
        }
        if (!result || !result.tractsS3Id) {
            console.log(result ? 'No tracts file uploaded' : 'No data found');
            setMapBlock({
                kind: 'not-configured',
                step: 'map-record',
                finding: result ? 'no tracts file uploaded' : 'no map set up for this project',
                at: new Date(),
            });
            setIsDataLoaded(true);
            return;
        }

        if (result.apiKey) {
            setApiKey(result.apiKey);
            console.log(apiKey);
        } else {
            const defaultApiKey = process.env.REACT_APP_GIS_DEFAULT_API_KEY;
            setApiKey(defaultApiKey);
            console.log('api key', apiKey);
        }

        if (result.mapId) {
            setMapId(result.mapId);
            console.log(mapId);
        } else {
            const defaultMapId = process.env.REACT_APP_GIS_DEFAULT_MAP_ID;
            setMapId(defaultMapId);
            console.log('map id', mapId);
        }

        // One uploaded file as a blob URL, or how its request failed.
        const fetchS3File = async (s3Id: number): Promise<{ url?: string; outcome: IRequestOutcome }> => {
            await get(`/v1.0/s3Bucket/by-id/${s3Id}`);
            const outcome = requestOutcome(mapDataRequest.error);
            if (outcome.failed) {
                return { outcome };
            }
            const blob = await response.blob();
            return { url: URL.createObjectURL(blob), outcome };
        };

        if (result.configS3Id) {
            const config = await fetchS3File(result.configS3Id);
            if (config.url) {
                setConfigUrl(config.url);
            } else {
                console.log('Failed to fetch config file:', config.outcome.status ?? 'no response');
                setMapNotice({ kind: 'config-failed', step: 'config-file', status: config.outcome.status, at: new Date() });
            }
        } else {
            // No notice: without a configuration the map just uses its default colors and layers.
            console.log('No config file uploaded');
        }

        const tracts = await fetchS3File(result.tractsS3Id);
        if (tracts.url) {
            setTractsUrl(tracts.url);
        } else {
            console.log('Failed to fetch tracts file:', tracts.outcome.status ?? 'no response');
            setMapBlock(loadFailureIssue(tracts.outcome, 'tracts-file'));
        }

        if (result.aoiS3Id) {
            const aoi = await fetchS3File(result.aoiS3Id);
            if (aoi.url) {
                setAoiUrl(aoi.url);
            } else {
                console.log('Failed to fetch aoi file:', aoi.outcome.status ?? 'no response');
            }
        } else {
            console.log('No aoi file uploaded');
        }
        setIsDataLoaded(true);
    };

    React.useEffect(() => {
        getMapData();
    }, []);

    // Parse tracts from GeoJSON when tractsUrl is available
    React.useEffect(() => {
        if (!tractsUrl) {
            return;
        }
        fetch(tractsUrl)
            .then((res) => res.json())
            .then((data) => {
                if (data?.features) {
                    const tracts = data.features.map((f: { properties: Record<string, string | number> }) => f.properties);
                    setAllTracts(tracts);
                }
            })
            .catch((err) => {
                console.log('Failed to parse tracts GeoJSON:', err);
                handleMapLoadFailed('tracts-read');
            });
    }, [tractsUrl, handleMapLoadFailed]);

    const containerRef = React.useRef<HTMLDivElement>(null);
    const [containerHeight, setContainerHeight] = React.useState<number | undefined>(undefined);

    React.useEffect(() => {
        const measure = () => {
            if (containerRef.current) {
                const top = containerRef.current.getBoundingClientRect().top;
                setContainerHeight(window.innerHeight - top);
            }
        };
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, [isDataLoaded]);

    // Page chrome below the map (ancestor padding/margins) still pushes the window into
    // scrolling even at innerHeight - top. Once the height is applied, give back any overflow.
    React.useLayoutEffect(() => {
        if (containerHeight === undefined) {
            return;
        }
        const overflow = document.documentElement.scrollHeight - window.innerHeight;
        if (overflow > 0) {
            setContainerHeight(Math.max(200, containerHeight - overflow));
        }
    }, [containerHeight]);

    return (
        <div
            ref={containerRef}
            style={{
                width: '100%',
                maxWidth: '100%',
                height: containerHeight ? `${containerHeight}px` : 'auto',
                display: 'flex',
                flexDirection: 'column',
                margin: 0,
                padding: 0,
                boxSizing: 'border-box',
                overflow: 'hidden',
            }}
        >
            {isDataLoaded && (
                <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', margin: 0, padding: 0, position: 'relative' }}>
                    <div style={{ flex: 1, minHeight: 0, display: 'flex', position: 'relative', overflow: 'hidden' }}>
                        {/* Each part of the page stands alone: one that hits an error while it draws shows a note in its
                            place, and the rest keeps working (see SoftErrorBoundary). */}
                        <SoftErrorBoundary what="map" className="soft-error-map">
                            <MapLegacy
                                apiKey={
                                    'AAPTa1K4lzyGPdK04T66smDLtkA..3cuuwI_EgO5wlBK-1qMp-M8ZayKvTmaly3iu-SG5uy9byezWb62O7Hu1wwbL0V7-kEEgC_Tawg1WfteYm1QOiAImwi1H4QM4XU-VBIDEQgBazXEksrhJUE_3cw2hVT32ekxfY3q4k2Ruex8GilDa7aHDwu69HULuu49S2tva-lilx48mpvnUUsduLw2ouN0F9w_-HOHjrGcGLTEHFMEdR-Ip9mwxOmk832yhq3uChdYZGRzF885dcxhNLEzAilU0kgK19r8.AT1_ahdWVNfM'
                                }
                                id={'03315601c8734388a63034b750430f8a'}
                                mapProperties={{}}
                                viewProperties={LOWER_48_VIEW}
                                tractsUrl={tractsUrl}
                                aoiUrl={aoiUrl}
                                configUrl={configUrl}
                                onSelectKuduTractID={handleSelectKuduTractID}
                                onTractSelect={handleTractSelect}
                                selectTractRef={selectTractRef}
                                zoomToTractRef={zoomToTractRef}
                                selectedTractIds={selectedTractIds}
                                onSelectedTractIdsChange={setSelectedTractIds}
                                onContactCategoryColors={setContactCategoryColors}
                                onActiveTractLayerChange={setActiveTractLayerId}
                                onTractLayersChange={setTractLayerIds}
                                onLeaseholdColors={setLeaseholdColors}
                                onContractLayerColors={handleContractLayerColors}
                                onTitleStatusColors={setTitleStatusColors}
                                onLoadFailed={handleMapLoadFailed}
                            />
                        </SoftErrorBoundary>
                        {mapNotice && !mapBlock && <MapNotice issue={mapNotice} onDismiss={() => setMapNotice(undefined)} />}
                        <SoftErrorBoundary what="tract panel" resetKey={selectedKuduTractID} className="soft-error-panel">
                            <TractDetailPanel
                                title={tractTitle}
                                contactCategoryColors={activeTractLayerId === 'tracts-contact-category' ? contactCategoryColors : undefined}
                                leaseholdColors={activeTractLayerId === 'tracts-leasehold' ? leaseholdColors : undefined}
                                brineColors={
                                    isBrineLayerId(activeTractLayerId)
                                        ? contractLayerColors[contractLayerPrefixOf(activeTractLayerId) || '']
                                        : undefined
                                }
                                titleStatusColors={activeTractLayerId === 'tracts-title-status' ? titleStatusColors : undefined}
                                // The panel's tabs follow the map's layers, and it opens on the one for the
                                // layer showing (see TractDetailPanel).
                                activeLayerId={activeTractLayerId}
                                tractLayerIds={tractLayerIds}
                                morOwners={morOwners}
                                morLeases={morLeases}
                                morBrineLeases={morBrineLeases}
                                onMorShown={handleMorShown}
                                tractId={selectedKuduTractID}
                                geoJsonAttributes={selectedAttributes}
                                tractDetail={tractDetail}
                                mineralOwnerships={mineralOwnerships}
                                leaseholds={leaseholdData}
                                leaseInfo={leaseInfo}
                                onLeaseholdsShown={handleLeaseholdsShown}
                                onLoadContactNotes={handleLoadContactNotes}
                                isLoadingDetail={isLoadingDetail}
                                isLoadingOwnerships={isLoadingOwnerships}
                                isLoadingLeaseholds={isLoadingLeaseholds}
                                loadNote={tractNote}
                                isCollapsed={isDetailCollapsed}
                                onToggleCollapse={() => setIsDetailCollapsed(!isDetailCollapsed)}
                                onNavigateToTractPage={handleNavigateToTractPage}
                                onZoomToTract={() => {
                                    const kuduId = String(selectedAttributes?.KuduTractID ?? selectedKuduTractID ?? '');
                                    if (kuduId) {
                                        zoomToTractRef.current?.(kuduId);
                                    }
                                }}
                            />
                        </SoftErrorBoundary>
                    </div>

                    <SoftErrorBoundary what="tract list" className="soft-error-list">
                        <TractAttributeTable
                            tractId={selectedKuduTractID}
                            geoJsonAttributes={selectedAttributes}
                            tractDetail={tractDetail}
                            mineralOwnerships={mineralOwnerships}
                            leaseholds={leaseholdData}
                            isLoadingDetail={isLoadingDetail}
                            isLoadingOwnerships={isLoadingOwnerships}
                            allTracts={allTracts}
                            onSelectTract={(id, attrs) => {
                                handleTractSelect(id, attrs);
                                if (selectTractRef.current) {
                                    selectTractRef.current(id);
                                }
                            }}
                            isLoadingLeaseholds={isLoadingLeaseholds}
                            isCollapsed={isTableCollapsed}
                            onToggleCollapse={() => setIsTableCollapsed(!isTableCollapsed)}
                            selectedTractIds={selectedTractIds}
                            onSelectedTractIdsChange={setSelectedTractIds}
                            tractLayerIds={tractLayerIds}
                        />
                    </SoftErrorBoundary>

                    {/* Over everything: the map, its panels and the tract list. Trying again reloads the
                        page, since the map loads its tracts only when it first mounts. */}
                    {mapBlock && (
                        <MapBlockOverlay
                            issue={mapBlock}
                            details={errorDetails(mapBlock, `Project: ${appContext.clientNumber}-${appContext.projectNumber}`)}
                            onRetry={() => window.location.reload()}
                        />
                    )}
                </div>
            )}
        </div>
    );
};

export default MappingPage;



// WEBPACK FOOTER //
// ./src/components/project/sections/Mapping.tsx